import { Geolocation, PositionOptions } from '@capacitor/geolocation';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { KeepAwake } from '@capacitor-community/keep-awake';
import MainScene from 'src/app/Scenes/MainScene';
import { GlobalService, ModeState } from 'src/app/services/global.service';
import { LatLonToXYConverter } from './LatLonToXY';
import { showToast } from './Utilities';

const keepAwake = async () => {
  if (Capacitor.getPlatform() === 'web') return;
  try { await KeepAwake.keepAwake(); } catch (e) { console.warn('KeepAwake failed', e); }
};

const allowSleep = async () => {
  if (Capacitor.getPlatform() === 'web') return;
  try { await KeepAwake.allowSleep(); } catch (e) { console.warn('AllowSleep failed', e); }
};

const isSupported = async () => {
  if (Capacitor.getPlatform() === 'web') return false;
  try { const result = await KeepAwake.isSupported(); return result?.isSupported ?? false; } catch { return false; }
};

export class GpsModeManager {
  mainScene: MainScene;
  deviceOrientation: PluginListenerHandle;
  positionOptions: PositionOptions = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 0,
  };
  watchId: string;
  compassSubscription: any;
  compassHandler: EventListenerOrEventListenerObject | null = null;
  motionHandler: EventListenerOrEventListenerObject | null = null;
  isIOS: RegExpMatchArray;
  isConnecting: boolean = false;
  isConnected: boolean = false;

  constructor(mainScene: MainScene) {
    this.mainScene = mainScene;
    // this.deviceOrientation = new DeviceOrientation();
    this.isIOS =
    navigator.userAgent.match(/(iPod|iPhone|iPad)/) &&
    navigator.userAgent.match(/AppleWebKit/);
  }

  async startPositionWatch() {
    console.log('Starting position watch');

    // Request permission to access location (native)
    const permissionStatus = await Geolocation.requestPermissions();
    console.log(`Geolocation permission status: ${permissionStatus.location}`);
    if (permissionStatus.location !== 'granted') {
      await showToast({ message: 'Location permission not granted', color: 'danger' });
      throw new Error('Location permission not granted');
    }

    this.watchId = await Geolocation.watchPosition(
      this.positionOptions,
      (position, error) => {
        try {
          if (error) {
            console.error('Native watchPosition error', error);
            return;
          }
          if (position) {
            if (this.isConnected) {
              LatLonToXYConverter.goToPosition(position, true);
              this.mainScene.updateGeoPosText(position);
            }
            console.log('Position Data from watch', position);
          } else {
            console.log('Position from watchLocation is null');
          }
        } catch (error) {
          console.error(`Error retrieving GPS data: ${error}`);
          showToast({ message: `Error retrieving GPS data: ${error}`, color: 'danger' });
        }
      }
    );
  }


  compassHeading(event: DeviceOrientationEvent) {

    // Convert degrees to radians
    var alphaRad = event.alpha * (Math.PI / 180);
    var betaRad = event.beta * (Math.PI / 180);
    var gammaRad = event.gamma * (Math.PI / 180);
  
    // Calculate equation components
    var cA = Math.cos(alphaRad);
    var sA = Math.sin(alphaRad);
    var cB = Math.cos(betaRad);
    var sB = Math.sin(betaRad);
    var cG = Math.cos(gammaRad);
    var sG = Math.sin(gammaRad);
  
    // Calculate A, B, C rotation components
    var rA = - cA * sG - sA * sB * cG;
    var rB = - sA * sG + cA * sB * cG;
    var rC = - cB * cG;
  
    // Calculate compass heading
    var compassHeading = Math.atan(rA / rB);
  
    // Convert from half unit circle to whole unit circle
    if(rB < 0) {
      compassHeading += Math.PI;
    }else if(rA < 0) {
      compassHeading += 2 * Math.PI;
    }
  
    // Convert radians to degrees
    compassHeading *= 180 / Math.PI;
  
    return compassHeading;
  
  }

  async startCompassWatch() {
    console.log('Starting compass watch');

    // iOS permission flow
    if ((DeviceOrientationEvent as any).requestPermission) {
      try {
        const perm = await (DeviceOrientationEvent as any).requestPermission();
        console.log(`DeviceOrientationEvent permission: ${perm}`);
        if (perm !== 'granted') throw new Error('Permission not granted for DeviceOrientationEvent');
      } catch {
        console.log('Error requesting device orientation permission');
        throw new Error('Error requesting device orientation permission');
      }
    }

    // handler (used for both deviceorientationabsolute and deviceorientation)
    const handler = (event: DeviceOrientationEvent) => {
      // Determine source and heading
      let headingDeg: number | null = null;
      let source = 'unknown';

      // WebKit direct heading (iOS)
      if ((event as any).webkitCompassHeading !== undefined && (event as any).webkitCompassHeading !== null) {
        headingDeg = (event as any).webkitCompassHeading as number; // 0..360
        source = 'webkitCompassHeading';
      }
      // deviceorientationabsolute or alpha available
      else if ((event as any).absolute === true || (event.alpha !== null && event.alpha !== undefined)) {
        // prefer alpha when available
        headingDeg = event.alpha ?? null; // 0..360 (may be relative)
        source = 'deviceorientation';
      }
      // fallback: try computing from alpha/beta/gamma
      if (headingDeg === null) {
        try {
          headingDeg = this.compassHeading(event);
          source = 'computed';
        } catch (e) {
          console.log('Unable to compute heading from event', e);
          return;
        }
      }

      // Normalize heading to 0..360
      headingDeg = ((headingDeg % 360) + 360) % 360;

      // Compensate for screen orientation (portrait/landscape) so heading maps correctly to screen
      const screenAngle = (typeof screen !== 'undefined' && (screen as any).orientation && (screen as any).orientation.angle) !== undefined
        ? (screen as any).orientation.angle
        : (window.orientation || 0);

      // Adjust heading by screen rotation and normalize
      let adjusted = (headingDeg - (screenAngle as number) + 360) % 360;
      // Convert to signed -180..180
      let signed = adjusted > 180 ? adjusted - 360 : adjusted;
      // Invert to match Phaser/camera rotation direction
      let phaserAngle: number = -signed;

      // console.log('Compass event', source, 'headingDeg:', Math.round(headingDeg), 'screenAngle:', screenAngle, 'phaserAngle:', Math.round(phaserAngle));

      if (this.isConnected) {
        this.mainScene.configureCameraForGPS();
        this.mainScene.cameras.main.setAngle(phaserAngle);
        this.mainScene.compassAngle = phaserAngle;
        this.mainScene.player.setAngle(-phaserAngle);
      } else {
        this.mainScene.configureCameraForJoystick();
        this.mainScene.cameras.main.setAngle(0);
        this.mainScene.player.setAngle(0);
        this.mainScene.compassAngle = 0;
      }
    };

    // devicemotion listener just for debugging / fallback
    const motionHandler = (ev: Event) => {
      // keep minimal logging
      // console.log('Device motion event', ev);
    };

    // Attach both listeners; some devices/browsers fire only one of them
    window.addEventListener('deviceorientationabsolute', handler, true);
    window.addEventListener('deviceorientation', handler, true);
    window.addEventListener('devicemotion', motionHandler, true);

    // store handlers to remove later
    this.compassHandler = handler;
    this.motionHandler = motionHandler;
  }

  async toggleGpsMode() {
    if (Capacitor.getPlatform() === 'web') {
      await showToast({ message: 'GPS Mode is only available in the app. Coming soon!', color: 'warning' });
      this.isConnected = false;
      this.isConnecting = false;
      return;
    }

    // Always reset camera to joystick mode first
    this.mainScene.configureCameraForJoystick();
    this.mainScene.cameras.main.setAngle(0);
    this.mainScene.player.setAngle(0);
    this.mainScene.compassAngle = 0;

    if (!this.isConnected && !this.isConnecting) {
      this.isConnecting = true;
      try{
        console.log('GPS Mode Activated');
        // Start watching for changes in GPS location
        await this.startPositionWatch();
  
        // Watch the device compass heading change
        await this.startCompassWatch();
        if (isSupported()) {
          keepAwake().then(
            () => console.log('keepAwake success'),
            () => console.log('keepAwake error')
          );
        }
  
        // If everything worked update the gps mode state to be on
        this.isConnecting = false;
        this.isConnected = true;
        GlobalService.$gpsModeState.next(GlobalService.modeState.On);
        await showToast({message: 'GPS Mode Activated', color: 'success'});
      }
      catch (e) {
        this.isConnecting = false;
        this.isConnected = false;
        GlobalService.$gpsModeState.next(GlobalService.modeState.Off);
        await showToast({message: 'Error starting GPS Mode.\nPlease ensure location permissions are granted and try again.', color: 'danger'});
      }
    }
    else if (this.isConnected) {
      console.log('GPS Mode Deactivated');

      // Stop watching GPS location
      Geolocation.clearWatch({ id: this.watchId });

      // Remove compass/listeners if present
      try {
        if (this.compassHandler) {
          window.removeEventListener('deviceorientationabsolute', this.compassHandler as EventListener, true);
          window.removeEventListener('deviceorientation', this.compassHandler as EventListener, true);
          this.compassHandler = null;
        }
        if (this.motionHandler) {
          window.removeEventListener('devicemotion', this.motionHandler as EventListener, true);
          this.motionHandler = null;
        }
      } catch (e) {
        console.warn('Error removing device orientation listeners', e);
      }

      if (isSupported()) {
        allowSleep().then(
          () => console.log('allowSleepAgain success'),
          () => console.log('allowSleepAgain error')
        );
      }
      this.isConnecting = false;
      this.isConnected = false;
      GlobalService.$gpsModeState.next(GlobalService.modeState.Off);
      await showToast({message: 'GPS Mode Deactivated', color: 'primary'});
    }
  }
}
